import { BubbleMenu as BaseBubbleMenu, useEditorState } from "@tiptap/react";
import deepEql from "fast-deep-equal";
import React, { useCallback } from "react";

import { MenuProps } from "../../../../components/menus/types";
import { getRenderContainer } from "../../../../utils";

import { Icon, Toolbar } from "utils/ui";

export const TableAlignMenu = React.memo(({ editor }: MenuProps): JSX.Element => {
  const getReferenceClientRect = useCallback(() => {
    // .tableWrapper is the DOM wrapper @tiptap/extension-table's built-in nodeview puts around
    // every <table> — walking up to it (rather than using the cursor's own rect, the BubbleMenu
    // default) is what anchors this menu to the table itself regardless of where the selection
    // happens to sit inside it.
    const renderContainer = getRenderContainer(editor, "tableWrapper");
    return renderContainer?.getBoundingClientRect() || new DOMRect(-1000, -1000, 0, 0);
  }, [editor]);

  const shouldShow = useCallback(() => editor.isActive("table"), [editor]);

  const { isAlignLeft, isAlignCenter, isAlignRight } = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isAlignLeft: editor.isActive("table", { align: "left" }),
      isAlignCenter: editor.isActive("table", { align: "center" }),
      isAlignRight: editor.isActive("table", { align: "right" }),
    }),
    equalityFn: deepEql,
  });

  const onAlignLeft = useCallback(() => {
    editor.chain().focus(undefined, { scrollIntoView: false }).setTableAlign("left").run();
  }, [editor]);

  const onAlignCenter = useCallback(() => {
    editor.chain().focus(undefined, { scrollIntoView: false }).setTableAlign("center").run();
  }, [editor]);

  const onAlignRight = useCallback(() => {
    editor.chain().focus(undefined, { scrollIntoView: false }).setTableAlign("right").run();
  }, [editor]);

  return (
    <BaseBubbleMenu
      editor={editor}
      pluginKey="tableAlignMenu"
      shouldShow={shouldShow}
      updateDelay={0}
      tippyOptions={{
        // Same appendTo as TableRowMenu/TableColumnMenu — #root (React's actual event-
        // delegation root), not document.body and not the scrolling editor canvas. See those
        // components for the full explanation.
        appendTo: () => document.getElementById("root") ?? document.body,
        placement: "bottom",
        offset: [0, 8],
        zIndex: 1000,
        popperOptions: {
          modifiers: [{ name: "flip", enabled: false }],
        },
        getReferenceClientRect,
      }}
    >
      <Toolbar.Wrapper>
        <Toolbar.Button tooltip="Align left" active={isAlignLeft} onClick={onAlignLeft}>
          <Icon name="AlignHorizontalDistributeStart" />
        </Toolbar.Button>
        <Toolbar.Button tooltip="Align center" active={isAlignCenter} onClick={onAlignCenter}>
          <Icon name="AlignHorizontalDistributeCenter" />
        </Toolbar.Button>
        <Toolbar.Button tooltip="Align right" active={isAlignRight} onClick={onAlignRight}>
          <Icon name="AlignHorizontalDistributeEnd" />
        </Toolbar.Button>
      </Toolbar.Wrapper>
    </BaseBubbleMenu>
  );
});

TableAlignMenu.displayName = "TableAlignMenu";

export default TableAlignMenu;
